/**
 * Vendor Flex++ structure → backend Flex++ structure, and answers back.
 * (docs/vendor-apps-contract.md §3.1)
 *
 * The backend answers Flex++ (`Type: 'Flex'` on /v1/conversation/question) in
 * the shape of the structure it was sent, and natively understands `_type` of
 * `string` / `number` / `integer` (see LambdaAPI FlexPlusPlus.md). We do not
 * touch the backend's structured-output code; the vendor sugar is compiled
 * away here:
 *
 *   - shorthand leaves  'boolean — was RTM discussed?'  → { _choices: ['true','false'], _description }
 *   - `boolean` → a two-value enum (the backend's "Not Discussed" sentinel becomes null)
 *   - `string[]` / `number[]` leaves → a flex++ list with a one-field prototype
 *     row (`[{ value: {...} }]`), which the backend sizes to the transcript; we
 *     flatten the rows back to a plain array on the way in.
 *
 * Coercion on the way back is belt-and-braces: the schema already forces the
 * primitive types, but a partial / chunked answer can still hand back strings.
 */

export const TYPES = ['boolean', 'number', 'integer', 'string', 'string[]', 'number[]'];

/** An empty structure ('' / null / {}) asks for one plain-text summary instead of a shape. */
export const SUMMARY_KEY = 'summary';
export const SUMMARY_PROMPT =
  'A short plain-text summary (2-4 sentences) of what happened in this visit that is relevant to this app, given the context it provided. Include what the patient reported, what the clinician did or changed, and the plan. Empty string if nothing relevant was said.';
export const isEmptyStructure = (s) =>
  s == null || (typeof s === 'string' && !s.trim()) || (typeof s === 'object' && !Array.isArray(s) && Object.keys(s).length === 0);
const LEAF_KEYS = ['_type', '_description', '_choices', '_existingValue', '_multiple'];
const SHORTHAND = /^\s*(boolean|number|string|string\[\]|number\[\])\s+[—–-]\s+([\s\S]*)$/;
const LIST_VALUE_KEY = 'value';

const isLeafObject = (v) =>
  v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0 && Object.keys(v).every((k) => k.charAt(0) === '_');

/** Parse a shorthand leaf. An untyped string is a string prompt. */
export function parseShorthand(value) {
  if (typeof value !== 'string') return null;
  const m = value.match(SHORTHAND);
  if (m) return { type: m[1], description: m[2].trim() };
  return { type: 'string', description: value.trim() };
}

function scalarLeaf({ _type, _description, _choices, _existingValue, _multiple }) {
  const type = TYPES.includes(_type) ? _type : 'string';
  const leaf = {};
  if (_description && String(_description).trim()) leaf._description = String(_description).trim();
  if (_choices) {
    // Enum-constrained leaves are typed by their choices on the backend.
    leaf._choices = _choices;
    if (_multiple === true) leaf._multiple = true; // native multi-select → array of choices
  } else if (type === 'boolean') {
    // flex++ has no boolean; a two-value enum is the same thing on the wire and
    // the backend's single-select sentinel ("Not Discussed" → null) gives us a
    // real "not discussed" instead of a forced false. Coerced back to true/false.
    leaf._choices = ['true', 'false'];
  } else {
    leaf._type = type; // string | number — native flex++
  }
  if (_existingValue !== undefined) leaf._existingValue = _existingValue;
  return leaf;
}

/** `string[]` / `number[]` → list with a one-field prototype row. */
function arrayLeaf(type, node) {
  const item = type === 'number[]' ? 'number' : 'string';
  const row = {};
  row[LIST_VALUE_KEY] = scalarLeaf({
    _type: item,
    _description: node._description ? `${node._description} (one item per row)` : 'one item per row',
    _choices: node._choices,
  });
  return [row];
}

/**
 * @returns {{ structure: object, types: Record<string,string> }}
 *   `structure` is what goes to the backend (with the `flex++` / `prune_early`
 *   markers the injectors set). `types` maps a leaf path — `pain.now`,
 *   `hep_changes[].action` — to its declared type for coercion.
 */
export function compileStructure(structure, { pruneEarly = true } = {}) {
  const types = {};
  if (isEmptyStructure(structure)) {
    const compiled = { [SUMMARY_KEY]: { _type: 'string', _description: SUMMARY_PROMPT }, 'flex++': true };
    if (pruneEarly) compiled.prune_early = true;
    return { structure: compiled, types: { [SUMMARY_KEY]: 'string' }, summary: true };
  }

  function leaf(node, path) {
    const type = TYPES.includes(node._type) ? node._type : 'string';
    // A multi-select enum comes back as an array of the chosen strings.
    types[path] = node._choices && node._multiple === true ? 'string[]' : type;
    if (type === 'string[]' || type === 'number[]') return arrayLeaf(type, node);
    return scalarLeaf(node);
  }

  function walk(node, path) {
    if (typeof node === 'string') {
      const { type, description } = parseShorthand(node);
      return leaf({ _type: type, _description: description }, path);
    }
    if (Array.isArray(node)) {
      return node.map((row) => walk(row, `${path}[]`));
    }
    if (isLeafObject(node)) return leaf(node, path);
    const out = {};
    for (const [key, value] of Object.entries(node || {})) {
      if (key === '_existingValue') {
        // row-level marker on a list row: pass through untouched
        out[key] = value;
        continue;
      }
      if (key === '_description' && typeof value === 'string' && value.trim()) {
        // Branch-level description: flex++ sets it once on the object schema.
        out._description = value.trim();
        continue;
      }
      if (LEAF_KEYS.includes(key)) continue; // stray leaf key on a branch — drop
      out[key] = walk(value, path ? `${path}.${key}` : key);
    }
    return out;
  }

  const compiled = walk(structure, '');
  compiled['flex++'] = true;
  if (pruneEarly) compiled.prune_early = true;
  return { structure: compiled, types, summary: false };
}

/** Summary-mode answers are delivered as the string itself. */
export function unwrapSummary(answers) {
  const v = answers && typeof answers === 'object' ? answers[SUMMARY_KEY] : answers;
  return v == null ? '' : String(v).trim();
}

const splitList = (v) =>
  Array.isArray(v)
    ? v
    : typeof v === 'string'
      ? v.split(/\r?\n|;|,(?=\s)/).map((s) => s.trim()).filter(Boolean)
      : v == null
        ? []
        : [v];

/** Rows from an array leaf come back as [{ value }] — flatten before list coercion. */
const unwrapRows = (v) =>
  Array.isArray(v) ? v.map((row) => (row && typeof row === 'object' && !Array.isArray(row) && LIST_VALUE_KEY in row ? row[LIST_VALUE_KEY] : row)) : v;

export function coerceValue(value, type) {
  if (value === undefined) return null;
  switch (type) {
    case 'boolean': {
      if (typeof value === 'boolean') return value;
      const s = String(value ?? '').trim().toLowerCase();
      if (['true', 'yes', 'y', '1'].includes(s)) return true;
      if (['false', 'no', 'n', '0'].includes(s)) return false;
      return null;
    }
    case 'number':
    case 'integer': {
      if (typeof value === 'number') return Number.isFinite(value) ? (type === 'integer' ? Math.round(value) : value) : null;
      const m = String(value ?? '').match(/-?\d+(\.\d+)?/);
      if (!m) return null;
      const n = parseFloat(m[0]);
      return type === 'integer' ? Math.round(n) : n;
    }
    case 'string[]':
      return splitList(unwrapRows(value)).map((s) => (s == null ? '' : String(s).trim())).filter(Boolean);
    case 'number[]':
      return splitList(unwrapRows(value)).map((s) => coerceValue(s, 'number')).filter((n) => n !== null);
    case 'string':
    default:
      if (value === null) return null;
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value).trim();
  }
}

/**
 * Walk the backend's answer alongside the type map and coerce every typed
 * leaf. Unknown paths (backend added something) pass through untouched.
 */
export function coerceAnswers(answers, types) {
  function walk(node, path) {
    if (Object.prototype.hasOwnProperty.call(types, path)) return coerceValue(node, types[path]);
    if (Array.isArray(node)) return node.map((row) => walk(row, `${path}[]`));
    if (node && typeof node === 'object') {
      const out = {};
      for (const [key, value] of Object.entries(node)) {
        if (key === 'flex++' || key === 'prune_early') continue;
        out[key] = walk(value, path ? `${path}.${key}` : key);
      }
      return out;
    }
    return node;
  }
  return walk(answers ?? {}, '');
}

/** Stable, order-independent hash of a structure — the cache key half that isn't contextVersion. */
export function hashStructure(structure) {
  const stable = (v) => {
    if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`;
    if (v && typeof v === 'object') {
      return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${stable(v[k])}`).join(',')}}`;
    }
    return JSON.stringify(v);
  };
  const s = stable(structure);
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}
